import {defineMessages} from 'react-intl'

/**
 * Catálogo compartido de la ficha de usuario (`oneUser/`).
 *
 * Mismo patrón que `channelConfig/channelConfigMessages.ts`: los componentes
 * importan `ONE_USER_MESSAGES as M` y formatean con `intl.formatMessage(M.x)`.
 * Vivir en un solo sitio evita que dos pestañas de la misma ficha inventen dos
 * llaves para el mismo texto.
 */
export const ONE_USER_MESSAGES = defineMessages({
  // --- Ajustes / zona peligrosa ---
  dangerousZone: {id: 'admin.oneUser.settings.dangerousZone', defaultMessage: 'Dangerous zone'},

  // --- Permisos ---
  permissionsTitle: {id: 'admin.oneUser.permissions.title', defaultMessage: 'Permissions'},
  permissionsTitleFor: {
    id: 'admin.oneUser.permissions.titleFor',
    defaultMessage: 'Permissions — {name}',
  },
  permissionsFallbackName: {
    id: 'admin.oneUser.permissions.fallbackName',
    defaultMessage: 'User {id}',
  },

  // --- Cambio de contraseña ---
  changePasswordTitle: {id: 'admin.oneUser.password.title', defaultMessage: 'Change Password'},
  passwordRequirements: {
    id: 'admin.oneUser.password.requirements',
    defaultMessage: 'Password requirements:',
  },
  toastPasswordUpdated: {
    id: 'admin.oneUser.password.toast.updated',
    defaultMessage: 'Password updated successfully',
  },
  pleaseWait: {id: 'admin.oneUser.pleaseWait', defaultMessage: 'Please wait...'},

  // --- Ficha del usuario ---
  notFound: {id: 'admin.oneUser.notFound', defaultMessage: 'Not found'},
  breadcrumbViewUser: {id: 'admin.oneUser.breadcrumb.view', defaultMessage: 'View user'},
  notRecorded: {id: 'admin.oneUser.notRecorded', defaultMessage: 'N/R'},
  editProfile: {id: 'admin.oneUser.action.editProfile', defaultMessage: 'Edit Profile'},
  deductions: {id: 'admin.oneUser.action.deductions', defaultMessage: 'Deductions'},

  // --- Seguridad de acceso ---
  loginSecurity: {id: 'admin.oneUser.security.title', defaultMessage: 'Login Security'},
  require2fa: {id: 'admin.oneUser.security.require2fa', defaultMessage: 'Require 2FA setup'},
  require2faHintEnabled: {
    id: 'admin.oneUser.security.require2faHintEnabled',
    defaultMessage: 'If enabled, this user will not be able to disable 2FA.',
  },
  require2faHintDisabled: {
    id: 'admin.oneUser.security.require2faHintDisabled',
    defaultMessage:
      'If enabled, the user will be redirected to profile and must configure 2FA on the next login.',
  },
  forcePasswordChange: {
    id: 'admin.oneUser.security.forcePasswordChange',
    defaultMessage: 'Force password change on next login',
  },
  forcePasswordChangeHint: {
    id: 'admin.oneUser.security.forcePasswordChangeHint',
    defaultMessage: 'If enabled, this user must change their password before accessing the system.',
  },
  toastNoSecurityChanges: {
    id: 'admin.oneUser.security.toast.noChanges',
    defaultMessage: 'No login security changes to save.',
  },
  toastSecurityUpdated: {
    id: 'admin.oneUser.security.toast.updated',
    defaultMessage: 'Login security updated.',
  },
  toastSecurityError: {
    id: 'admin.oneUser.security.toast.error',
    defaultMessage: 'Error updating setting: {message}',
  },

  // --- Resumen del perfil ---
  profileDetails: {id: 'admin.oneUser.overview.title', defaultMessage: 'Profile Details'},
  fullName: {id: 'admin.oneUser.overview.fullName', defaultMessage: 'Full Name'},
  npn: {id: 'admin.oneUser.overview.npn', defaultMessage: 'NPN'},
  company: {id: 'admin.oneUser.overview.company', defaultMessage: 'Company'},
  contactPhone: {id: 'admin.oneUser.overview.contactPhone', defaultMessage: 'Contact Phone'},
  phoneMustBeActive: {
    id: 'admin.oneUser.overview.phoneMustBeActive',
    defaultMessage: 'Phone number must be active',
  },
  verified: {id: 'admin.oneUser.overview.verified', defaultMessage: 'Verified'},
  companySite: {id: 'admin.oneUser.overview.companySite', defaultMessage: 'Company Site'},
  country: {id: 'admin.oneUser.overview.country', defaultMessage: 'Country'},
  countryOfOrigination: {
    id: 'admin.oneUser.overview.countryOfOrigination',
    defaultMessage: 'Country of origination',
  },
  communication: {id: 'admin.oneUser.overview.communication', defaultMessage: 'Communication'},

  // --- Edicion del perfil ---
  avatar: {id: 'admin.oneUser.profile.avatar', defaultMessage: 'Avatar'},
  changeAvatar: {id: 'admin.oneUser.profile.changeAvatar', defaultMessage: 'Change avatar'},
  capturePhotoTooltip: {
    id: 'admin.oneUser.profile.capturePhotoTooltip',
    defaultMessage: 'Capture photo from camera',
  },
  removeAvatar: {id: 'admin.oneUser.profile.removeAvatar', defaultMessage: 'Remove avatar'},
  allowedFileTypes: {
    id: 'admin.oneUser.profile.allowedFileTypes',
    defaultMessage: 'Allowed file types: png, jpg, jpeg, webp.',
  },
  capturePhoto: {id: 'admin.oneUser.profile.capturePhoto', defaultMessage: 'Capture Photo'},
  toastCameraError: {
    id: 'admin.oneUser.profile.toast.cameraError',
    defaultMessage: 'Unable to access camera. Please check permissions.',
  },
  toastPhotoCaptured: {
    id: 'admin.oneUser.profile.toast.photoCaptured',
    defaultMessage: 'Photo captured successfully',
  },
  toastUserUpdated: {
    id: 'admin.oneUser.profile.toast.userUpdated',
    defaultMessage: 'User updated successfully',
  },
  toastUserAvatarUpdated: {
    id: 'admin.oneUser.profile.toast.userAvatarUpdated',
    defaultMessage: 'User and avatar updated successfully',
  },

  // --- Ajustes personales del agente ---
  psIntro: {
    id: 'admin.oneUser.personal.intro',
    defaultMessage:
      'Overrides exclusive to this agent. These take priority over organization defaults.',
  },
  psSaving: {id: 'admin.oneUser.personal.saving', defaultMessage: 'Saving...'},
  psSaveButton: {
    id: 'admin.oneUser.personal.saveButton',
    defaultMessage: 'Save Personal Settings',
  },
  psSaveUserFirst: {
    id: 'admin.oneUser.personal.saveUserFirst',
    defaultMessage: 'Save the user first before configuring personal settings.',
  },
  psChannelsTitle: {
    id: 'admin.oneUser.personal.channelsTitle',
    defaultMessage: 'Communication Channels',
  },
  psAddChannel: {id: 'admin.oneUser.personal.addChannel', defaultMessage: 'Add Channel'},
  psAddTelephony: {id: 'admin.oneUser.personal.addTelephony', defaultMessage: 'Add Telephony'},
  psChannelLimit: {
    id: 'admin.oneUser.personal.channelLimit',
    defaultMessage: 'This user already has a WhatsApp and an SMS channel',
  },
  psNoChannels: {
    id: 'admin.oneUser.personal.noChannels',
    defaultMessage:
      "No personal channels configured — this agent inherits the organization's channels.",
  },
  psThType: {id: 'admin.oneUser.personal.th.type', defaultMessage: 'Type'},
  psThProvider: {id: 'admin.oneUser.personal.th.provider', defaultMessage: 'Provider'},
  psThWebhook: {id: 'admin.oneUser.personal.th.webhook', defaultMessage: 'Webhook'},
  psActive: {id: 'admin.oneUser.personal.active', defaultMessage: 'Active'},
  psInactive: {id: 'admin.oneUser.personal.inactive', defaultMessage: 'Inactive'},
  psCopyWebhook: {id: 'admin.oneUser.personal.copyWebhook', defaultMessage: 'Copy webhook URL'},
  psChannelVoice: {id: 'admin.oneUser.personal.channel.voice', defaultMessage: 'Voice'},
  psOtherSettings: {id: 'admin.oneUser.personal.otherSettings', defaultMessage: 'Other Settings'},
  psNoOtherSettings: {
    id: 'admin.oneUser.personal.noOtherSettings',
    defaultMessage: 'No additional settings configured for this agent yet.',
  },
  psEmailSmtp: {id: 'admin.oneUser.personal.emailSmtp', defaultMessage: 'Email (SMTP)'},
  psEmailSmtpShort: {id: 'admin.oneUser.personal.emailSmtpShort', defaultMessage: 'Email SMTP'},
  psAiDify: {id: 'admin.oneUser.personal.aiDify', defaultMessage: 'AI (Dify)'},
  psConfigured: {id: 'admin.oneUser.personal.configured', defaultMessage: 'Configured'},
  psDisabled: {id: 'admin.oneUser.personal.disabled', defaultMessage: 'Disabled'},
  psEnable: {id: 'admin.oneUser.personal.enable', defaultMessage: 'Enable'},
  psDisable: {id: 'admin.oneUser.personal.disable', defaultMessage: 'Disable'},

  // --- Modal SMTP ---
  psEnableSmtp: {id: 'admin.oneUser.personal.enableSmtp', defaultMessage: 'Enable SMTP override'},
  psSmtpHost: {id: 'admin.oneUser.personal.smtpHost', defaultMessage: 'SMTP Host'},
  psPort: {id: 'admin.oneUser.personal.port', defaultMessage: 'Port'},
  psUseTls: {id: 'admin.oneUser.personal.useTls', defaultMessage: 'Use TLS'},
  psUsernameEmail: {id: 'admin.oneUser.personal.usernameEmail', defaultMessage: 'Username / Email'},
  psFromAddress: {id: 'admin.oneUser.personal.fromAddress', defaultMessage: 'From Address'},
  psReplyTo: {id: 'admin.oneUser.personal.replyTo', defaultMessage: 'Reply-To (Optional)'},
  psTestConnection: {id: 'admin.oneUser.personal.testConnection', defaultMessage: 'Test Connection'},

  // --- Modal telefonia ---
  psTelephonySystem: {
    id: 'admin.oneUser.personal.telephonySystem',
    defaultMessage: 'Telephony System',
  },
  psTelephonyProvider: {
    id: 'admin.oneUser.personal.telephonyProvider',
    defaultMessage: 'Telephony Provider',
  },
  psAgentCredentials: {
    id: 'admin.oneUser.personal.agentCredentials',
    defaultMessage: 'Agent Credentials',
  },
  psAgentPassword: {id: 'admin.oneUser.personal.agentPassword', defaultMessage: 'Agent Password'},
  psAgentUser: {id: 'admin.oneUser.personal.agentUser', defaultMessage: 'Agent User'},

  // --- Cartera, transacciones y deducciones ---
  walletAllTransactions: {
    id: 'admin.oneUser.wallet.allTransactions',
    defaultMessage: 'All transactions',
  },
  walletAddTransaction: {
    id: 'admin.oneUser.wallet.addTransaction',
    defaultMessage: '+ transactions',
  },
  walletThValue: {id: 'admin.oneUser.wallet.th.value', defaultMessage: 'Value'},
  walletThBalance: {id: 'admin.oneUser.wallet.th.balance', defaultMessage: 'Balance'},
  walletIncome: {id: 'admin.oneUser.wallet.income', defaultMessage: 'Income'},
  walletExpense: {id: 'admin.oneUser.wallet.expense', defaultMessage: 'Expense'},
  walletNoData: {id: 'admin.oneUser.wallet.noData', defaultMessage: 'There is no data'},
  walletBalance: {id: 'admin.oneUser.wallet.balance', defaultMessage: 'Balance'},
  walletTransactions: {id: 'admin.oneUser.wallet.transactions', defaultMessage: 'Transactions'},
  walletNoWallet: {
    id: 'admin.oneUser.wallet.noWallet',
    defaultMessage: 'This user does not have a wallet or transactions',
  },
  deductionsTitle: {id: 'admin.oneUser.deductions.title', defaultMessage: 'Deductions'},
  deductionAdd: {id: 'admin.oneUser.deductions.add', defaultMessage: 'Add Deduction'},

  // --- Dify personal ---
  difyTitle: {id: 'admin.oneUser.dify.title', defaultMessage: 'Personal Dify Settings'},
  difyOrgEnabled: {
    id: 'admin.oneUser.dify.orgEnabled',
    defaultMessage:
      'Your organization has Dify enabled. You will use that configuration by default.',
  },
  difyUseOwn: {id: 'admin.oneUser.dify.useOwn', defaultMessage: 'Use my own Dify credentials'},
  difyUseOwnHint: {
    id: 'admin.oneUser.dify.useOwnHint',
    defaultMessage:
      'If enabled, your messages will route through your own Dify account, allowing you to use custom models and settings.',
  },
  difyApiKey: {id: 'admin.oneUser.dify.apiKey', defaultMessage: 'API Key'},
  difyHide: {id: 'admin.oneUser.dify.hide', defaultMessage: 'Hide'},
  difyShow: {id: 'admin.oneUser.dify.show', defaultMessage: 'Show'},
  difyApiKeyHint: {
    id: 'admin.oneUser.dify.apiKeyHint',
    defaultMessage: 'Get your API Key from your <a>Dify dashboard</a>',
  },
  difyApiUrl: {id: 'admin.oneUser.dify.apiUrl', defaultMessage: 'API URL'},
  difyApiUrlHint: {id: 'admin.oneUser.dify.apiUrlHint', defaultMessage: 'Default: {url}'},
  difyAppId: {id: 'admin.oneUser.dify.appId', defaultMessage: 'App ID'},
  difyAppIdPlaceholder: {
    id: 'admin.oneUser.dify.appIdPlaceholder',
    defaultMessage: 'Application UUID in Dify',
  },
  difyAppIdHint: {
    id: 'admin.oneUser.dify.appIdHint',
    defaultMessage: 'Required to route messages to the correct App.',
  },
  difyTestConnection: {id: 'admin.oneUser.dify.testConnection', defaultMessage: 'Test Connection'},
  difyImportant: {id: 'admin.oneUser.dify.important', defaultMessage: 'Important:'},
  difyStoredSecurely: {
    id: 'admin.oneUser.dify.storedSecurely',
    defaultMessage: 'Your API Key will be stored securely.',
  },
  difySaving: {id: 'admin.oneUser.dify.saving', defaultMessage: 'Saving...'},
  difySaveChanges: {id: 'admin.oneUser.dify.saveChanges', defaultMessage: 'Save changes'},
  difyNote: {id: 'admin.oneUser.dify.note', defaultMessage: 'Note:'},
  difyUsingOrg: {
    id: 'admin.oneUser.dify.usingOrg',
    defaultMessage: "You are currently using your organization's configuration.",
  },
  difyNotEnabled: {
    id: 'admin.oneUser.dify.notEnabled',
    defaultMessage:
      'Dify is not enabled for your organization. Contact your administrator to enable it.',
  },
  difyToastEnterApiKey: {
    id: 'admin.oneUser.dify.toast.enterApiKey',
    defaultMessage: 'Please enter your API Key',
  },
  difyToastSaved: {
    id: 'admin.oneUser.dify.toast.saved',
    defaultMessage: 'Dify configuration saved',
  },
  difyToastSaveError: {
    id: 'admin.oneUser.dify.toast.saveError',
    defaultMessage: 'Error saving configuration: {message}',
  },
  difyToastEnterKeyAndUrl: {
    id: 'admin.oneUser.dify.toast.enterKeyAndUrl',
    defaultMessage: 'Please enter API Key and URL',
  },
  difyToastVerified: {
    id: 'admin.oneUser.dify.toast.verified',
    defaultMessage: 'Dify connection verified',
  },

  // --- Config de canales del usuario ---
  ucToastSaved: {
    id: 'admin.oneUser.channelConfig.toast.saved',
    defaultMessage: 'User channel & Dify settings saved successfully',
  },
  ucToastSaveError: {
    id: 'admin.oneUser.channelConfig.toast.saveError',
    defaultMessage: 'Error saving user settings: {message}',
  },

  // --- Ajustes de correo del usuario ---
  ueToastTestSent: {
    id: 'admin.oneUser.emailSettings.toast.testSent',
    defaultMessage: 'Test email sent to {email}',
  },
  ueToastTestError: {
    id: 'admin.oneUser.emailSettings.toast.testError',
    defaultMessage: 'Error sending test email',
  },
  ueToastSaved: {
    id: 'admin.oneUser.emailSettings.toast.saved',
    defaultMessage: 'Email settings saved',
  },
  ueToastSaveError: {
    id: 'admin.oneUser.emailSettings.toast.saveError',
    defaultMessage: 'Error saving email settings',
  },
  difyInvalidApiKey: {id: 'admin.oneUser.dify.invalidApiKey', defaultMessage: 'Invalid API Key'},
  difyConnectionError: {
    id: 'admin.oneUser.dify.connectionError',
    defaultMessage: 'Connection error',
  },
  ucAccessRestricted: {
    id: 'admin.oneUser.channelConfig.accessRestricted',
    defaultMessage: 'Access Restricted',
  },
  ucAccessRestrictedBody: {
    id: 'admin.oneUser.channelConfig.accessRestrictedBody',
    defaultMessage: 'Only administrators can manage channel and PolicyDial settings.',
  },
  ucTabPersonal: {
    id: 'admin.oneUser.channelConfig.tab.personal',
    defaultMessage: 'Personal Settings',
  },
  ucTabOrganization: {
    id: 'admin.oneUser.channelConfig.tab.organization',
    defaultMessage: 'Organization Channels',
  },
  ucTabSuffix: {id: 'admin.oneUser.channelConfig.tab.suffix', defaultMessage: '({name})'},

  // --- Canales de la organizacion (solo lectura) ---
  ocNoOrg: {
    id: 'admin.oneUser.orgChannels.noOrg',
    defaultMessage: 'No Organization Assigned',
  },
  ocNoOrgBody: {
    id: 'admin.oneUser.orgChannels.noOrgBody',
    defaultMessage: 'This user must be assigned to an organization to view channel settings.',
  },
  ocIntro: {
    id: 'admin.oneUser.orgChannels.intro',
    defaultMessage: 'These settings apply to all agents in the organization.',
  },
  ocTwilioSms: {id: 'admin.oneUser.orgChannels.twilioSms', defaultMessage: 'Twilio SMS'},
  ocSmsIntegration: {
    id: 'admin.oneUser.orgChannels.smsIntegration',
    defaultMessage: 'SMS messaging integration',
  },
  ocPhoneNumber: {id: 'admin.oneUser.orgChannels.phoneNumber', defaultMessage: 'Phone Number'},
  ocStatusCallbacks: {
    id: 'admin.oneUser.orgChannels.statusCallbacks',
    defaultMessage: 'Status Callbacks:',
  },
  ocCopyStatusCallback: {
    id: 'admin.oneUser.orgChannels.copyStatusCallback',
    defaultMessage: 'Copy status callback URL',
  },
  ocCopyWebhook: {
    id: 'admin.oneUser.orgChannels.copyWebhook',
    defaultMessage: 'Copy full webhook URL',
  },
  ocNotConfigured: {
    id: 'admin.oneUser.orgChannels.notConfigured',
    defaultMessage: 'Not configured',
  },
  ocNotSet: {id: 'admin.oneUser.orgChannels.notSet', defaultMessage: 'Not set'},
  ocWhatsAppNumber: {
    id: 'admin.oneUser.orgChannels.whatsAppNumber',
    defaultMessage: 'WhatsApp Number',
  },
  ocBotmakerLine: {
    id: 'admin.oneUser.orgChannels.botmakerLine',
    defaultMessage: 'Botmaker Phone Line',
  },
  ocMetaPhoneId: {
    id: 'admin.oneUser.orgChannels.metaPhoneId',
    defaultMessage: 'Meta Phone Number ID',
  },
  ocEmailSmtp: {id: 'admin.oneUser.orgChannels.emailSmtp', defaultMessage: 'Email SMTP'},
  ocEmailIntegration: {
    id: 'admin.oneUser.orgChannels.emailIntegration',
    defaultMessage: 'Email messaging integration',
  },
  ocSmtpHost: {id: 'admin.oneUser.orgChannels.smtpHost', defaultMessage: 'SMTP Host'},
  ocPortTls: {id: 'admin.oneUser.orgChannels.portTls', defaultMessage: 'Port: {port} (TLS)'},
  ocPort: {id: 'admin.oneUser.orgChannels.port', defaultMessage: 'Port: {port}'},
  ocFromAddress: {id: 'admin.oneUser.orgChannels.fromAddress', defaultMessage: 'From Address'},
  ocVicidialIntegration: {
    id: 'admin.oneUser.orgChannels.vicidialIntegration',
    defaultMessage: 'VICIdial integration',
  },
  ocBaseUrl: {id: 'admin.oneUser.orgChannels.baseUrl', defaultMessage: 'Base URL'},
  ocCampaignId: {id: 'admin.oneUser.orgChannels.campaignId', defaultMessage: 'Campaign ID'},
  ocToastWebhookCopied: {
    id: 'admin.oneUser.orgChannels.toast.webhookCopied',
    defaultMessage: 'Webhook URL copied to clipboard',
  },
  ocToastStatusCopied: {
    id: 'admin.oneUser.orgChannels.toast.statusCopied',
    defaultMessage: 'Status callback URL copied',
  },
  ueIntro: {
    id: 'admin.oneUser.emailSettings.intro',
    defaultMessage:
      'Personal sender and signature for emails sent by this user. Fields without an override inherit from the Organization or the system.',
  },
  deductionModalAdd: {id: 'admin.oneUser.deductions.modal.add', defaultMessage: 'Add deduction'},
  deductionModalEdit: {id: 'admin.oneUser.deductions.modal.edit', defaultMessage: 'Edit deduction'},
  deductionSelectType: {
    id: 'admin.oneUser.deductions.selectType',
    defaultMessage: 'Select Deduction Type',
  },
  deductionDeleteTitle: {
    id: 'admin.oneUser.deductions.delete.title',
    defaultMessage: 'Delete deduction',
  },
  deductionDeleteBody: {
    id: 'admin.oneUser.deductions.delete.body',
    defaultMessage: 'Are you sure you want to delete this record?',
  },
  transactionModalAdd: {
    id: 'admin.oneUser.transactions.modal.add',
    defaultMessage: 'Add Transaction',
  },
  transactionSelectType: {
    id: 'admin.oneUser.transactions.selectType',
    defaultMessage: 'Select type',
  },
  toastCommissionRateUpdated: {
    id: 'admin.oneUser.deductions.toast.rateUpdated',
    defaultMessage: 'Commission rate updated successfully',
  },
  toastCreateFailed: {
    id: 'admin.oneUser.deductions.toast.createFailed',
    defaultMessage: 'Creation failed',
  },
  toastUpdateFailed: {
    id: 'admin.oneUser.deductions.toast.updateFailed',
    defaultMessage: 'Update failed',
  },
  toastDeductionDeleted: {
    id: 'admin.oneUser.deductions.toast.deleted',
    defaultMessage: 'Commission Deduction deleted successfully',
  },
  toastTransactionAdded: {
    id: 'admin.oneUser.transactions.toast.added',
    defaultMessage: 'Transaction added successfully',
  },
  psPhoneLogin: {
    id: 'admin.oneUser.personal.phoneLogin',
    defaultMessage: 'Extension / Phone Login',
  },
  psPhonePassword: {id: 'admin.oneUser.personal.phonePassword', defaultMessage: 'Phone Password'},
  psHidePassword: {id: 'admin.oneUser.personal.hidePassword', defaultMessage: 'Hide password'},
  psShowPassword: {id: 'admin.oneUser.personal.showPassword', defaultMessage: 'Show password'},
  psCampaignRouting: {
    id: 'admin.oneUser.personal.campaignRouting',
    defaultMessage: 'Campaign Routing',
  },
  psInheritOrgWith: {
    id: 'admin.oneUser.personal.inheritOrgWith',
    defaultMessage: 'Inherit org ({campaign})',
  },
  psInheritOrg: {id: 'admin.oneUser.personal.inheritOrg', defaultMessage: 'Inherit org'},
  psAutoDialDelay: {
    id: 'admin.oneUser.personal.autoDialDelay',
    defaultMessage: 'Auto Dial Delay (seconds)',
  },
  psResetInherited: {
    id: 'admin.oneUser.personal.resetInherited',
    defaultMessage: 'Reset to Inherited',
  },
  psMyNumbersHint: {
    id: 'admin.oneUser.personal.myNumbersHint',
    defaultMessage:
      'Shows the “My Numbers” tab in this user’s Profile so they can buy their own Twilio Caller IDs (inbound + outbound).',
  },
  psMaxNumbers: {id: 'admin.oneUser.personal.maxNumbers', defaultMessage: 'Max numbers'},

  // --- Modal HealthSherpa ---
  psAgentEmail: {id: 'admin.oneUser.personal.agentEmail', defaultMessage: 'Agent Email'},
  psMustMatchAgent: {
    id: 'admin.oneUser.personal.mustMatchAgent',
    defaultMessage: 'Must match registered agent',
  },
  psApiKey: {id: 'admin.oneUser.personal.apiKey', defaultMessage: 'API Key'},
  psSmtpPassword: {id: 'admin.oneUser.personal.smtpPassword', defaultMessage: 'Password'},
  psCampaign: {id: 'admin.oneUser.personal.campaign', defaultMessage: 'Campaign'},
  psAdd: {id: 'admin.oneUser.personal.add', defaultMessage: 'Add'},
  psWebhookSecret: {id: 'admin.oneUser.personal.webhookSecret', defaultMessage: 'Webhook Secret'},
  psCopied: {id: 'admin.oneUser.personal.copied', defaultMessage: 'Copied'},

  // --- Modal de prueba ---
  psTestWhatsApp: {id: 'admin.oneUser.personal.testWhatsApp', defaultMessage: 'Test WhatsApp'},
  psTestSms: {id: 'admin.oneUser.personal.testSms', defaultMessage: 'Test SMS'},
  psTestEmail: {id: 'admin.oneUser.personal.testEmail', defaultMessage: 'Test Email'},
  psDestinationEmail: {
    id: 'admin.oneUser.personal.destinationEmail',
    defaultMessage: 'Destination email',
  },
  psDestinationNumber: {
    id: 'admin.oneUser.personal.destinationNumber',
    defaultMessage: 'Destination number',
  },
  psCountryCodeHint: {
    id: 'admin.oneUser.personal.countryCodeHint',
    defaultMessage: 'Include country code, e.g. +1 for USA.',
  },
  psSending: {id: 'admin.oneUser.personal.sending', defaultMessage: 'Sending...'},
  psSendTest: {id: 'admin.oneUser.personal.sendTest', defaultMessage: 'Send Test'},

  // --- Toasts y confirmaciones ---
  psToastEnterRecipient: {
    id: 'admin.oneUser.personal.toast.enterRecipient',
    defaultMessage: 'Enter a recipient',
  },
  psToastWebhookCopied: {
    id: 'admin.oneUser.personal.toast.webhookCopied',
    defaultMessage: 'Webhook URL copied',
  },
  psToastChannelDeleted: {
    id: 'admin.oneUser.personal.toast.channelDeleted',
    defaultMessage: 'Channel deleted',
  },
  psToastChannelDeleteError: {
    id: 'admin.oneUser.personal.toast.channelDeleteError',
    defaultMessage: 'Error deleting channel',
  },
  psToastChannelSaved: {
    id: 'admin.oneUser.personal.toast.channelSaved',
    defaultMessage: 'Channel saved',
  },
  psToastChannelSaveError: {
    id: 'admin.oneUser.personal.toast.channelSaveError',
    defaultMessage: 'Error saving channel',
  },
  psToastTelephonyRemoved: {
    id: 'admin.oneUser.personal.toast.telephonyRemoved',
    defaultMessage: 'Telephony override removed',
  },
  psToastEmailRemoved: {
    id: 'admin.oneUser.personal.toast.emailRemoved',
    defaultMessage: 'Email override removed',
  },
  psToastHealthSherpaRemoved: {
    id: 'admin.oneUser.personal.toast.healthSherpaRemoved',
    defaultMessage: 'HealthSherpa override removed',
  },
  psToastAiRemoved: {
    id: 'admin.oneUser.personal.toast.aiRemoved',
    defaultMessage: 'AI override removed',
  },
  psConfirmTelephonyTitle: {
    id: 'admin.oneUser.personal.confirm.telephonyTitle',
    defaultMessage: 'Remove telephony override',
  },
  psConfirmTelephonyBody: {
    id: 'admin.oneUser.personal.confirm.telephonyBody',
    defaultMessage: 'This user will inherit the organization telephony settings.',
  },
  psConfirmEmailTitle: {
    id: 'admin.oneUser.personal.confirm.emailTitle',
    defaultMessage: 'Remove Email (SMTP) override',
  },
  psConfirmEmailBody: {
    id: 'admin.oneUser.personal.confirm.emailBody',
    defaultMessage: 'This will clear the personal SMTP override for this agent.',
  },
  psConfirmHealthSherpaTitle: {
    id: 'admin.oneUser.personal.confirm.healthSherpaTitle',
    defaultMessage: 'Remove HealthSherpa override',
  },
  psConfirmHealthSherpaBody: {
    id: 'admin.oneUser.personal.confirm.healthSherpaBody',
    defaultMessage: 'This will clear the personal HealthSherpa credentials for this agent.',
  },
  psConfirmAiTitle: {
    id: 'admin.oneUser.personal.confirm.aiTitle',
    defaultMessage: 'Remove AI (Dify) override',
  },
  psConfirmAiBody: {
    id: 'admin.oneUser.personal.confirm.aiBody',
    defaultMessage: 'This will clear the personal AI (Dify) configuration for this agent.',
  },
  psConfirmChannelTitle: {
    id: 'admin.oneUser.personal.confirm.channelTitle',
    defaultMessage: 'Delete channel',
  },
  psConfirmChannelBody: {
    id: 'admin.oneUser.personal.confirm.channelBody',
    defaultMessage: 'This channel configuration will be permanently removed.',
  },
})
